import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Search, Phone, Mail, Globe } from 'lucide-react';
import { getAccessToken } from '../../services/tokenStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

async function fetchLeads() {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/leads`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch leads');
  return res.json();
}

export default function AdminLeadsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-leads'],
    queryFn: fetchLeads,
  });

  const leads = data?.data?.leads || [];
  const filteredLeads = leads.filter((lead: any) => {
    const matchesSearch =
      lead.name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.email?.toLowerCase().includes(search.toLowerCase()) ||
      lead.phone?.includes(search) ||
      lead.website?.domain?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-500/20 text-blue-400';
      case 'contacted':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'qualified':
        return 'bg-purple-500/20 text-purple-400';
      case 'converted':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'lost':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-600 text-gray-300';
    }
  };

  const statuses = ['all', 'new', 'contacted', 'qualified', 'converted', 'lost'];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">All Leads</h1>
          <p className="text-gray-400 mt-1">{leads.length} total leads across all websites</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {statuses.map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg capitalize whitespace-nowrap transition-colors ${
                statusFilter === status
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No leads found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="text-left px-6 py-3 text-gray-300 font-medium">Lead</th>
                  <th className="text-left px-6 py-3 text-gray-300 font-medium">Contact</th>
                  <th className="text-left px-6 py-3 text-gray-300 font-medium">Website</th>
                  <th className="text-left px-6 py-3 text-gray-300 font-medium">Status</th>
                  <th className="text-left px-6 py-3 text-gray-300 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredLeads.map((lead: any) => (
                  <tr key={lead.id} className="hover:bg-gray-750">
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-gray-400 text-sm">{lead.source}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {lead.phone && (
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Phone size={14} />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Mail size={14} />
                            <span>{lead.email}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Globe size={14} />
                        <span>{lead.website?.domain || 'N/A'}</span>
                      </div>
                      {lead.website?.user && (
                        <p className="text-gray-500 text-xs mt-1">
                          Owner: {lead.website.user.email}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
